import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import {
	FrontendForm,
	GetFormsFrontendData
} from 'features/entry-form-v2/data/useGetFormsQuery/useGetFormsQuery';
import { GetProjectData } from 'features/entry-form-v2/data/useGetProjectQuery';
import {
	decodeDependencyData,
	DependencyData
} from 'features/entry-form-v2/data/useGetVariableDependenciesQuery/useGetVariableDependenciesQuery';
import {
	decodeVariablesData,
	VariablesData
} from 'features/entry-form-v2/data/useVariablesQuery/useVariablesQuery';
import {
	isCategoryMultipleNonFixedVariable,
	isCategoryNonFixedVariable
} from 'features/entry-form-v2/types';
import { ProjectMetadata, ProjectMetadataParameter } from 'store/data/projects/types';

export const PROJECT_METADATA_EXPORT_SCHEMA_VERSION = 1;

/**
 * A configuration-only snapshot of a project. It deliberately contains no entry/patient
 * data — only the form, variable and dependency configuration that describes the project.
 */
export type ProjectMetadataExport = {
	ledidiProjectMetadataExport: {
		schemaVersion: number;
		exportedAt: string;
		projectId: string;
		projectName: string;
	};
	forms: FrontendForm[];
	variables: VariablesData;
	dependencies: DependencyData;
	projectMetadata: {
		values: ProjectMetadata;
		definition: ProjectMetadataParameter[] | null;
	};
};

const fetchFormsConfig = async (projectId: string): Promise<FrontendForm[]> => {
	const result = await makeRequest<GetFormsFrontendData>({
		method: 'getForms',
		service: 'stats',
		data: {
			projectId: parseInt(projectId)
		}
	});

	return result.forms;
};

/**
 * Non-fixed category variables accumulate their `categories` list from values that have
 * actually been entered, so those values are entry-derived — not configuration. Strip them
 * so this config-only export never leaks entered data; the variable definition itself is kept.
 * (Fixed categories are design-time `allowedCategories`, so those stay.)
 */
const stripEntryDerivedCategoryValues = (data: VariablesData): VariablesData => ({
	...data,
	variables: Object.fromEntries(
		Object.entries(data.variables).map(([key, variable]) => [
			key,
			isCategoryNonFixedVariable(variable) || isCategoryMultipleNonFixedVariable(variable)
				? { ...variable, categories: [] }
				: variable
		])
	)
});

const fetchVariablesConfig = async (projectId: string): Promise<VariablesData> => {
	const result = await makeRequest<VariablesData>({
		method: 'getVariablesData',
		service: 'stats',
		data: {
			projectId: parseInt(projectId)
		}
	});

	return stripEntryDerivedCategoryValues(decodeVariablesData(result));
};

const fetchDependenciesConfig = async (projectId: string): Promise<DependencyData> => {
	const result = await makeRequest<DependencyData>({
		method: 'getVariableDependencies',
		service: 'cde',
		data: {
			projectId: parseInt(projectId)
		}
	});

	return decodeDependencyData(result);
};

const fetchProjectInfo = (projectId: string): Promise<GetProjectData> => {
	return makeRequest<GetProjectData>({
		method: 'getProject',
		service: 'projects',
		data: {
			project: {
				projectId: parseInt(projectId)
			},
			retrieveProjectOwnerDetails: true
		}
	});
};

export const gatherProjectMetadata = async ({
	projectId,
	metadataDefinition
}: {
	projectId: string;
	/** The instance-wide project metadata parameter schema, so the exported values are interpretable. */
	metadataDefinition: ProjectMetadataParameter[] | null;
}): Promise<ProjectMetadataExport> => {
	const [forms, variables, dependencies, projectInfo] = await Promise.all([
		fetchFormsConfig(projectId),
		fetchVariablesConfig(projectId),
		fetchDependenciesConfig(projectId),
		fetchProjectInfo(projectId)
	]);

	return {
		ledidiProjectMetadataExport: {
			schemaVersion: PROJECT_METADATA_EXPORT_SCHEMA_VERSION,
			exportedAt: new Date().toISOString(),
			projectId,
			projectName: projectInfo.project.projectName
		},
		forms,
		variables,
		dependencies,
		projectMetadata: {
			values: projectInfo.project.metadata ?? {},
			definition: metadataDefinition
		}
	};
};

export const buildExportFileName = (projectName: string, projectId: string): string => {
	const safeName = projectName
		.trim()
		.replace(/[^\p{L}\p{N}\-_]+/gu, '_')
		.replace(/^_+|_+$/g, '');

	const base = safeName.length > 0 ? safeName : `project-${projectId}`;

	return `${base}-metadata.json`;
};
